import { FC, useState } from "react";
import { Button, Center, Flex, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Dropzone } from "@mantine/dropzone";
import { uploadCV } from "../../api/userApi";
import useAuthStore from "../../store/useAuthStore";
import useQuestionnaireStore from "../../store/useQuestionnaireStore";

interface Props {
  onUpload: () => void;
}

export const UploadCVForm: FC<Props> = ({ onUpload }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const { access_token, onProcessingCv } = useAuthStore();
  const [loading, setLoading] = useState(false);

  const { fetchQuestions } = useQuestionnaireStore();

  const handleDrop = (files: File[]) => {
    const acceptedFormats = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];
    const file = files[0];

    if (file && acceptedFormats.includes(file.type)) {
      setFile(file);
      setFileError(null);
    } else {
      setFile(null);
      setFileError(t("upload_cv_form1.invalid_file_format"));
    }
  };

  const handleSubmit = async () => {
    setFileError(null);

    setLoading(true);
    if (file && access_token) {
      try {
        const response = await uploadCV({ cvFile: file });
        if (response) {
          const process = await onProcessingCv({ cvId: response.data.id });
          if (process.status === "success") {
            await fetchQuestions(access_token);
            setLoading(false);
            onUpload();
          }
        }
      } catch (error: any) {
        setFileError(error.response.data.error);
        setLoading(false);
      }
    }
  };

  const handleResetFile = () => {
    setFile(null);
    setFileError(null);
  };

  return (
    <Stack align="stretch" justify="center" gap="34px">
      {!file && (
        <Dropzone
          onDrop={handleDrop}
          accept={[
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "text/plain",
          ]}
          maxSize={5 * 1024 ** 2}
        >
          <Center h={70}>
            <Flex justify="center" direction="column" gap={10}>
              <Text c="dimmed" size="md" fw={700}>
                {t("upload_cv_form1.drag_and_click")}
              </Text>
              <Text c="dimmed" size="xs" fw={400}>
                {t("upload_cv_form1.accepted_formats")}
              </Text>
            </Flex>
          </Center>
        </Dropzone>
      )}

      {file && (
        <Text
          size="sm"
          color="dimmed"
          onClick={() => {
            if (!loading) handleResetFile();
          }}
          style={{ cursor: loading ? "not-allowed" : "pointer" }}
        >
          {t("upload_cv_form1.selected_file")}: {file.name}
        </Text>
      )}

      {fileError && (
        <Text size="sm" color="red">
          {fileError}
        </Text>
      )}

      {loading && (
        <Text size="sm" color="gray">
          {t("wait_cv_check")}
        </Text>
      )}

      <Button
        fullWidth
        onClick={handleSubmit}
        disabled={!file}
        loading={loading}
      >
        {t("upload_cv_form1.submit_button")}
      </Button>
    </Stack>
  );
};
